<p-confirmDialog [appendTo]="'body'" />

<!-- Rendered on body: embedded in the FA client page this sits inside the
     content splitter, which isolates its stacking context, so a dialog left in
     place is painted over by the shell chrome above it. -->
<p-dialog
  [visible]="visible()"
  (visibleChange)="visible.set($event)"
  [modal]="true"
  [draggable]="false"
  [dismissableMask]="true"
  [showHeader]="false"
  [appendTo]="'body'"
  [style]="{ width: '44rem', maxWidth: '95vw' }"
  styleClass="air-detail-dialog"
>
  <header class="air-detail__header">
    <div class="air-detail__header-top">
      @if (clientName() || activity().client_guid) {
        <span
          class="air-detail__client"
          [pTooltip]="activity().client_guid ?? ''"
          tooltipPosition="bottom"
        >
          <i class="pi pi-user"></i>
          {{ clientName() ?? activity().client_guid }}
        </span>
      } @else {
        <span class="air-detail__client air-detail__client--unknown"
          >No client on record</span
        >
      }

      <div class="air-detail__header-right">
        <span
          class="air-status"
          [ngClass]="statusMeta[activity().status].chipClass"
        >
          <span
            class="air-status__dot"
            [ngClass]="statusMeta[activity().status].dotClass"
          ></span>
          {{ statusMeta[activity().status].label }}
        </span>
        <button
          type="button"
          class="air-detail__close"
          aria-label="Close"
          (click)="close()"
        >
          <i class="pi pi-times"></i>
        </button>
      </div>
    </div>

    <h2 class="air-detail__title" data-testid="detail-title">
      {{ activity().title }}
    </h2>
    <p class="air-detail__description">{{ activity().description }}</p>
    <p class="air-detail__proposed">Proposed {{ proposedAgo() }}</p>
  </header>

  <div class="air-detail__body">
    <section>
      <h3 class="air-detail__section-label">
        Proposed actions
        <span class="air-detail__section-count">{{ actions().length }}</span>
        @if (actions().length > 0) {
          <span
            class="air-action__disposition"
            [ngClass]="dispositionMeta[rollup()].chipClass"
            pTooltip="Worst of the action dispositions below"
            tooltipPosition="bottom"
            data-testid="detail-rollup"
          >
            {{ dispositionMeta[rollup()].label }}
          </span>
        }
        @if (breakdown()) {
          <span class="air-detail__section-note">{{ breakdown() }}</span>
        }
      </h3>

      @if (actions().length === 0) {
        <p class="air-detail__no-actions">
          The router agent read this window and proposed nothing actionable.
        </p>
      }

      @for (action of actions(); track $index) {
        <article
          class="air-action"
          [attr.data-testid]="'detail-action-' + $index"
        >
          <div class="air-action__head">
            <span class="air-action__index">{{ $index + 1 }}</span>
            <span
              class="air-action__type"
              [ngClass]="actionTypeMeta[action.action_type].chipClass"
            >
              {{ actionTypeMeta[action.action_type].label }}
            </span>
            <h4 class="air-action__title">{{ action.title }}</h4>
            <span
              class="air-action__disposition"
              [ngClass]="dispositionMeta[action.disposition].chipClass"
            >
              {{ dispositionMeta[action.disposition].label }}
            </span>
          </div>

          <p class="air-detail__field-label">Instruction</p>
          <p class="air-action__instruction">{{ action.instruction }}</p>

          <button
            type="button"
            class="air-action__rationale-toggle"
            [attr.aria-expanded]="isRationaleOpen($index)"
            [attr.data-testid]="'detail-rationale-toggle-' + $index"
            (click)="toggleRationale($index)"
          >
            <i
              class="pi"
              [class.pi-angle-right]="!isRationaleOpen($index)"
              [class.pi-angle-down]="isRationaleOpen($index)"
            ></i>
            Rationale
          </button>
          @if (isRationaleOpen($index)) {
            <p
              class="air-action__rationale"
              [attr.data-testid]="'detail-rationale-' + $index"
            >
              {{ action.rationale }}
            </p>
          }
        </article>
      }
    </section>

    <section class="air-trigger">
      <div class="air-trigger__head">
        <span class="air-detail__field-label">
          <i class="pi pi-comment"></i>
          What triggered this
        </span>
        <span class="air-trigger__source">{{ triggerSource() }}</span>
      </div>

      @if (quote(); as triggerQuote) {
        <blockquote class="air-trigger__quote">{{ triggerQuote }}</blockquote>
      }

      @if (activity().conversation_link; as link) {
        <a
          class="air-trigger__link"
          [href]="link"
          target="_blank"
          rel="noopener noreferrer"
        >
          Open full conversation
          <i class="pi pi-external-link"></i>
        </a>
      }
    </section>

    @if (notes().length > 0) {
      <section class="air-notes" data-testid="detail-notes">
        <p class="air-detail__field-label">
          Past notes
          <span class="air-detail__section-count">{{ notes().length }}</span>
          <span class="air-detail__field-hint">oldest first</span>
        </p>
        <ol>
          @for (note of notes(); track $index) {
            <li>
              <p class="air-notes__meta">
                <span class="air-notes__actor">{{ note.actor }}</span>
                <span>{{ note.absolute }} &middot; {{ note.relative }}</span>
              </p>
              <p class="air-notes__text">{{ note.note }}</p>
            </li>
          }
        </ol>
      </section>
    }

    <section class="air-debug" #debugSection>
      <button
        type="button"
        class="air-debug__toggle"
        [attr.aria-expanded]="debugOpen()"
        data-testid="detail-debug-toggle"
        (click)="toggleDebug()"
      >
        Debug info
        <i
          class="pi"
          [class.pi-angle-down]="!debugOpen()"
          [class.pi-angle-up]="debugOpen()"
        ></i>
      </button>

      @if (debugOpen()) {
        <div class="air-debug__panel">
          <dl class="air-debug__rows">
            @for (row of debugRows(); track row.label) {
              <div class="air-debug__row">
                <dt>{{ row.label }}</dt>
                <dd>
                  @if (row.value) {
                    <span class="air-debug__value">{{ row.value }}</span>
                    <button
                      type="button"
                      class="air-debug__copy"
                      [attr.aria-label]="'Copy ' + row.label"
                      pTooltip="Copy"
                      tooltipPosition="left"
                      (click)="copy(row)"
                    >
                      <i class="pi pi-copy"></i>
                    </button>
                    @if (row.href) {
                      <a
                        [href]="row.href"
                        target="_blank"
                        rel="noopener noreferrer"
                        [attr.aria-label]="'Open ' + row.label"
                      >
                        <i class="pi pi-external-link"></i>
                      </a>
                    }
                  } @else {
                    <span class="air-debug__empty">not set</span>
                  }
                </dd>
              </div>
            }
          </dl>

          @if (citedParts().length > 0) {
            <div class="air-debug__parts" data-testid="detail-cited-parts">
              <p class="air-detail__field-label">
                Cited message parts
                <span class="air-detail__section-count">{{
                  citedParts().length
                }}</span>
              </p>
              <ul>
                @for (citation of citedParts(); track citation.part_id) {
                  <li>
                    <span class="air-debug__part-id">{{
                      citation.part_id
                    }}</span>
                    <span class="air-debug__part-body">
                      @if (citation.part_body) {
                        {{ citation.part_body }}
                      } @else {
                        <em>No excerpt provided</em>
                      }
                    </span>
                  </li>
                }
              </ul>
            </div>
          }
        </div>
      }
    </section>
  </div>

  <footer class="air-detail__footer">
    @if (settled(); as reason) {
      <p class="air-detail__settled" data-testid="detail-settled">
        <i class="pi pi-lock"></i>
        {{ reason }}
      </p>
    } @else {
      <label class="air-detail__field-label" for="air-note">
        Note
        <span class="air-detail__field-hint"
          >optional to confirm &middot; required to retry</span
        >
      </label>

      <textarea
        pTextarea
        id="air-note"
        rows="2"
        [autoResize]="true"
        class="air-detail__note"
        data-testid="detail-note"
        placeholder="Optional for Confirm. Required for Retry — this text is the feedback the router agent re-runs with."
        [value]="noteDraft()"
        (input)="noteDraft.set($any($event.target).value)"
      ></textarea>

      <div class="air-detail__actions">
        <div class="air-detail__buttons">
          <p-button
            icon="pi pi-check"
            [label]="
              actions().length > 0 ? 'Confirm ' + actions().length : 'Confirm'
            "
            size="small"
            [loading]="busy() === 'confirm'"
            [disabled]="actions().length === 0"
            data-testid="detail-confirm"
            (onClick)="requestAction('confirm')"
          />
          <p-button
            icon="pi pi-refresh"
            label="Retry"
            size="small"
            severity="secondary"
            [outlined]="true"
            [loading]="busy() === 'retry'"
            data-testid="detail-retry"
            (onClick)="requestAction('retry')"
          />
          <p-button
            label="Deny"
            size="small"
            severity="danger"
            [text]="true"
            [loading]="busy() === 'deny'"
            data-testid="detail-deny"
            (onClick)="requestAction('deny')"
          />
        </div>
        <p class="air-detail__footnote">
          Confirming queues this for execution — nothing is created until that
          step finishes.
        </p>
      </div>
    }
  </footer>
</p-dialog>
